// One person: who they are, who knows them, and every touch.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../../../lib'
import { usePaletteActions } from '../app/Palette'
import { Busy, Button, Copy, Empty, Err, Page, Section, dayOf, useKeys } from '../kit'
import { useMarkMessaged, useRecordActions } from './actions'
import { TRACK_LABEL, fillMessage, linkedinHref, nameOf, safeHref, statusOptions, statusWord, useMeta, type Imported, type Card, type Connection, type PageRow, type Rel, type Stages, type Sync, type Task } from './lib'
import { CardLine, PageLine, ReadLine, TimelineView, Walk, useWalk } from './parts'

type Person = {
  id: string; company_id: string | null; company: string | null; domain: string | null; do_not_contact: boolean | null; do_not_contact_reason: string | null
  name: string; title: string | null; email: string | null; phone: string | null; linkedin_url: string | null; app_user_id: string | null
}
type Send = { id: string; list: string; channel: string; message: string | null; link: string | null; state: string; done_at: string | null; done_by: string | null }
type Data = { imported: Imported; arrived: string | null; at: string; person: Person; connections: Connection[]; tasks: Task[]; relationships: Rel[]; pages: PageRow[]; sends: Send[]; card: Card | null; stages: Stages; sync: Sync }

export function PersonPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  useWalk('person', id, '/staff/people')
  const path = `/api/staff/crm/people/${id}`
  const q = useQuery({ queryKey: ['crm', 'person', id], queryFn: () => api<Data>('GET', path) })
  const d = q.data
  const meta = useMeta()
  const markMessaged = useMarkMessaged()
  const a = useRecordActions({ personId: id, companyId: d?.person.company_id, stages: d?.stages })
  const rels = d?.relationships ?? []
  const firstPage = d?.pages[0]?.url

  const status = async () => {
    const founders = meta.data?.owners ?? []
    const me = meta.data?.me ?? d?.connections[0]?.founder ?? founders[0] ?? ''
    const cur = d?.connections.find((k) => k.founder === me)
    const v = await a.open({
      title: 'Connection', fields: [
        { key: 'founder', label: 'Founder', kind: 'select', value: me, required: true, options: founders.map((f) => ({ value: f, label: nameOf(f) })) },
        { key: 'status', label: 'Status', kind: 'select', value: cur?.status ?? '', options: statusOptions(cur?.status) },
        { key: 'note', label: 'Note', kind: 'long', value: cur?.note ?? '' },
      ],
    })
    if (v) await a.write('POST', `${path}/connection`, { founder: v.founder, ...(v.status !== (cur?.status ?? '') ? { status: v.status || null } : {}), note: v.note.trim() || null }, 'Saved')
  }

  const messaged = async () => {
    const founder = meta.data?.me ?? d?.connections[0]?.founder ?? meta.data?.owners[0]
    if (founder) await markMessaged(id, founder)
  }

  useKeys({ n: () => void a.note(), s: () => void a.stage(rels), t: () => void a.task(), c: () => void a.copyPage(firstPage) }, !!d)
  usePaletteActions('person', d ? [
    { id: 'note', label: `Add a note on ${d.person.name}`, hint: 'n', run: () => void a.note() },
    { id: 'touch', label: 'Log a touch', run: () => void a.touch() },
    { id: 'stage', label: 'Change stage', hint: 's', run: () => void a.stage(rels) },
    { id: 'task', label: 'Add a task', hint: 't', run: () => void a.task() },
    { id: 'conn', label: 'Set connection status', run: () => void status() },
    { id: 'edit', label: 'Edit person', run: () => void a.editPerson(d.person) },
    { id: 'copy', label: 'Copy page link', hint: 'c', run: () => void a.copyPage(firstPage) },
    ...(d.tasks.filter((t) => !t.done_at).map((t) => ({ id: `finish-${t.id}`, label: `Finish task: ${t.title}`, run: () => void a.finishTask(t.id) }))),
    { id: 'audit', label: 'Open the audit log for this person', run: () => navigate(`/staff/log?object_id=${id}`) },
  ] : [])

  if (q.error) return <Page title="Person"><Err error={q.error} /></Page>
  if (!d) return <Page title="Person"><Busy /></Page>
  const p = d.person
  const open = d.tasks.filter((t) => !t.done_at)
  return (
    <Page
      testid="st-person"
      title={p.name}
      sub={<ReadLine at={d.at} sync={d.sync} what="Record" imported={d.imported} />}
      right={(
        <>
          <Walk kind="person" id={id} base="/staff/people" />
          <Button onClick={() => void a.note()} keyHint="n" data-testid="st-act-note">Note</Button>
          <Button onClick={() => void a.touch()} data-testid="st-act-touch">Log touch</Button>
          <Button onClick={() => void a.task()} keyHint="t" data-testid="st-act-task">Task</Button>
          <Button onClick={() => void status()} data-testid="st-act-conn">Connection</Button>
          <Button onClick={() => void messaged()} data-testid="st-act-messaged">Mark messaged</Button>
          <Button onClick={() => void a.editPerson(p)} data-testid="st-act-edit">Edit</Button>
        </>
      )}
    >
      {p.do_not_contact ? <div className="st-banner bad" role="alert" data-testid="st-dnc">Do not contact{p.do_not_contact_reason ? `: ${p.do_not_contact_reason}` : ''}</div> : null}
      <dl className="st-facts st-rec-facts" data-testid="st-person-facts">
        <dt>Works at</dt><dd>{[p.title, p.company_id ? null : p.company].filter(Boolean).join(', ')}{p.company_id ? <>{p.title ? ', ' : ''}<Link className="st-link" to={`/staff/companies/${p.company_id}`}>{p.company}</Link></> : null}{!p.title && !p.company_id ? '–' : null}</dd>
        <dt>Email</dt><dd>{p.email ? <a className="st-link" href={`mailto:${p.email}`}>{p.email}</a> : '–'}</dd>
        <dt>Phone</dt><dd>{p.phone ?? '–'}</dd>
        <dt>LinkedIn</dt><dd><a className="st-link" href={linkedinHref(p.linkedin_url, p.name, p.company)} target="_blank" rel="noreferrer" data-testid="st-linkedin">{safeHref(p.linkedin_url) ? 'Open profile' : 'Search for them'}</a></dd>
        <dt>Tracks</dt><dd>{rels.length ? rels.map((r) => `${TRACK_LABEL[r.track]}: ${r.stage_label}`).join(', ') : '–'}</dd>
        <dt>Card</dt><dd><CardLine card={d.card} /></dd>
        <dt>Came from</dt><dd data-testid="st-arrived">{d.arrived ?? '–'}</dd>
        <dt>Account</dt><dd>{p.app_user_id ? <Link className="st-link" to={`/staff/person/${p.app_user_id}`} data-testid="st-raw-person">Raw person page</Link> : 'Not signed up'}</dd>
      </dl>

      <div className="st-two st-rec-two">
        <TimelineView path={path} personal />
        <div className="st-stack st-rec-side">
          <Section title="Connections" count={d.connections.length}>
            {d.connections.length ? d.connections.map((k) => (
              <div key={k.founder} className="st-rec-line" data-testid="st-connection">
                <span className="st-rec-main">{nameOf(k.founder)}{k.claimed ? <span className="st-muted"> by word, no export yet</span> : null}</span>
                <span className="st-muted">{[statusWord(k.status), k.connected_on ? `since ${dayOf(k.connected_on)}` : null, k.note].filter(Boolean).join(', ') || 'No status'}</span>
              </div>
            )) : <Empty>No founder knows them yet.</Empty>}
          </Section>
          <Section title="On send lists" count={d.sends.length}>
            {d.sends.length ? d.sends.map((s) => (
              <div key={s.id} className="st-rec-line" data-testid="st-send-row">
                <span className="st-rec-main"><Link className="st-link" to={`/staff/send?list=${encodeURIComponent(s.list)}&group=all&state=all`}>{s.list}</Link></span>
                <span className="st-muted">{s.state === 'queued' ? 'To do' : `${nameOf(s.state)}${s.done_by ? ` by ${nameOf(s.done_by)}` : ''}${s.done_at ? `, ${dayOf(s.done_at)}` : ''}`}</span>
                {s.message ? <Copy text={fillMessage(s.message, { link: s.link, person: p.name, company: p.company })} label="Copy DM" testid="st-person-dm" /> : null}
              </div>
            )) : <Empty>On no list.</Empty>}
          </Section>
          <Section title="Pages" count={d.pages.length} right={d.pages.length && !d.pages.some((g) => g.checked_at) ? <span className="st-fresh" data-testid="st-pages-unchecked">Not checked</span> : undefined}>
            {d.pages.length ? d.pages.map((g) => <PageLine key={g.id} page={g} />) : <Empty>No page yet.</Empty>}
          </Section>
          <Section title="Tasks" count={open.length}>
            {open.length ? open.map((t) => (
              <div key={t.id} className="st-rec-line" data-testid="st-task">
                <span className="st-rec-main">{t.title}</span>
                <span className="st-muted">{[nameOf(t.owner), t.due_on ? `due ${dayOf(t.due_on)}` : null].filter(Boolean).join(', ')}</span>
                <Button tone="quiet" onClick={() => void a.finishTask(t.id)} data-testid="st-task-done">Finish</Button>
              </div>
            )) : <Empty>No open tasks.</Empty>}
          </Section>
        </div>
      </div>
      {a.node}
    </Page>
  )
}
